"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { XIcon } from "lucide-react";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StickToBottomContext } from "use-stick-to-bottom";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAiChatUiContext } from "@/lib/ai/ai-chat-ui-context";
import { type AgentUIMessage } from "@/lib/ai/types";
import { useAuthorization } from "@/lib/authorization/authorization-context";
import { useSharedAnalysisTargetOptions } from "./analysis-target-options-context";
import { ChatComposer } from "./chat-composer";
import { ChatExternalErrorCard } from "./chat-external-error-card";
import { ChatMessages } from "./chat-messages";
import { ChatQuotaStatusCard } from "./chat-quota-status-card";
import { useSharedChatQuotaStatus } from "./chat-quota-status-context";
import {
  resolveChatQuotaStatusCardState,
  resolveChatQuotaTierIndicatorState,
} from "./chat-quota-view-state";
import { ChatStarter } from "./chat-starter";
import { ConversationPositionReporter } from "./conversation-position-reporter";
import { TokenUsageIndicator } from "./token-usage-indicator";
import { useAnalysisTargetPageContextSync } from "./use-analysis-target-page-context";
import { useChatDisclaimerOcclusion } from "./use-chat-disclaimer-occlusion";
import { useChatThread } from "./use-chat-thread";

type Props = {
  workspaceId: string;
  chatId?: string | null;
  initialMessages?: AgentUIMessage[];
  creatorName?: string | null;
  creatorPicture?: string | null;
  isCreatedByViewer?: boolean;
  isLoading?: boolean;
  compactStarterWhenInactive?: boolean;
  onChatCreated?: (chatId: string) => void;
  isVisible?: boolean;
};

const EMPTY_MESSAGES: AgentUIMessage[] = [];
const CHAT_SURFACE_CLASS_NAME =
  "mx-auto w-full max-w-[72rem] px-4 @sm:px-5 @xl:px-6 @4xl:px-8";

type ChatErrorKind = "context-limit" | "overloaded" | "generic";

function detectChatErrorKind(message: string): ChatErrorKind {
  const normalized = message.toLowerCase();
  if (
    normalized.includes("prompt is too long") ||
    normalized.includes("token count exceeds") ||
    normalized.includes("token limit") ||
    normalized.includes("context length") ||
    normalized.includes("maximum number of tokens")
  ) {
    return "context-limit";
  }

  if (
    normalized.includes("overloaded") ||
    normalized.includes("capacity") ||
    normalized.includes("temporarily unavailable") ||
    normalized.includes("retry")
  ) {
    return "overloaded";
  }

  return "generic";
}

/**
 * chat の表示・入力・権限・エラー表示を組み立てる画面境界。
 *
 * 送受信と永続化の順序制御は `useChatThread` に委譲し、ここではその状態を
 * Starter・Conversation・Composer のどれへ投影するかだけを決める。
 */
export function ChatThread({
  workspaceId,
  chatId: propChatId,
  initialMessages = EMPTY_MESSAGES,
  creatorName,
  creatorPicture,
  isCreatedByViewer,
  isLoading,
  compactStarterWhenInactive = true,
  onChatCreated,
  isVisible = true,
}: Props) {
  const [isConversationAtBottom, setIsConversationAtBottom] = useState(true);
  const [positionedConversationKey, setPositionedConversationKey] = useState<
    string | null
  >(null);
  const chatThreadRef = useRef<HTMLDivElement>(null);
  const { i18n } = useLingui();
  const {
    currentModel: model,
    selectModel,
    aiModelAccess,
    clearChatSelection,
    lang,
    analysisTargets,
    pageAnalysisTarget,
    manualAnalysisTargets,
    setPageAnalysisTarget,
  } = useAiChatUiContext();
  const { currentUser } = useAuthorization();
  const pathname = usePathname();
  const optionsState = useSharedAnalysisTargetOptions();
  const { lowQuotaStatus, highQuotaStatus, reexecuteQuotaStatusQuery } =
    useSharedChatQuotaStatus();
  const lowQuotaIndicator = useMemo(
    () =>
      resolveChatQuotaTierIndicatorState({
        tier: "LOW",
        aiModelAccess,
        lowQuotaStatus,
        highQuotaStatus,
      }),
    [aiModelAccess, lowQuotaStatus, highQuotaStatus],
  );
  const highQuotaIndicator = useMemo(
    () =>
      resolveChatQuotaTierIndicatorState({
        tier: "HIGH",
        aiModelAccess,
        lowQuotaStatus,
        highQuotaStatus,
      }),
    [aiModelAccess, lowQuotaStatus, highQuotaStatus],
  );
  /**
   * query/effect owner を `ChatThread` に固定し、Starter <-> Composer 切り替え時の
   * render-phase update を防ぐ。
   */
  const { currentPageContext, suppressPageContextAutoSyncOnCurrentPath } =
    useAnalysisTargetPageContextSync({
      shouldSyncPageContext: isVisible,
      pathname,
      options: optionsState.options,
      selectedTargets: analysisTargets,
      setPageSelectedTarget: setPageAnalysisTarget,
    });

  const {
    messages,
    status,
    stop,
    addToolResult,
    addToolApprovalResponse,
    canRetryApprovalContinuation,
    isRetryingApprovalContinuation,
    retryApprovalContinuation,
    retryableApprovalToolCallId,
    sendMessage,
    requestApprovalContinuation,
    handleSend,
    startNewChat,
    canSendMessages,
    appError,
    externalError,
    error,
    clearError,
    usage,
    activeChatId,
    isCreating,
    isPersistingMessages,
  } = useChatThread({
    workspaceId,
    activeChatId: propChatId,
    initialMessages,
    model,
    aiModelAccess,
    pageAnalysisTarget,
    manualAnalysisTargets,
    currentPageContext,
    isCreatedByViewer,
    onCreate: onChatCreated ?? (() => {}),
  });
  const previousChatStatusRef = useRef(status);
  useEffect(() => {
    const previousStatus = previousChatStatusRef.current;
    previousChatStatusRef.current = status;

    if (!workspaceId || !isVisible) {
      return;
    }

    if (
      status === "ready" &&
      previousStatus !== "ready" &&
      previousStatus !== "error"
    ) {
      reexecuteQuotaStatusQuery({
        requestPolicy: "network-only",
      });
    }
  }, [isVisible, workspaceId, status, reexecuteQuotaStatusQuery]);
  const chatUrl = useMemo(() => {
    if (!lang) return null;
    const encodedWorkspaceId = encodeURIComponent(workspaceId);
    const chatId = activeChatId ?? propChatId ?? null;
    const chatPath = chatId
      ? `/${lang}/${encodedWorkspaceId}/ai/${encodeURIComponent(chatId)}`
      : `/${lang}/${encodedWorkspaceId}/ai`;
    if (typeof window === "undefined") {
      return chatPath;
    }
    return window.location.origin + chatPath;
  }, [lang, workspaceId, activeChatId, propChatId]);
  const messageLogContext = useMemo(
    () => ({
      workspaceId,
      chatId: activeChatId ?? propChatId ?? null,
      model,
      chatUrl,
    }),
    [activeChatId, chatUrl, model, propChatId, workspaceId],
  );

  // 既存 chat は取得済みの作成者情報を使う。draft から作成した chat は、
  // 作成後に activeChatId が付いても、この画面を開いた利用者が作成者だと扱う。
  const finalCreatorName = propChatId ? creatorName : currentUser?.name;
  const finalCreatorPicture = propChatId
    ? creatorPicture
    : currentUser?.picture;
  const rawUserVisibleErrors = useMemo(
    () => new Set([i18n._(msg`Only the creator can send messages`)]),
    [i18n],
  );
  const errorKind = useMemo(
    () => (error ? detectChatErrorKind(error) : null),
    [error],
  );
  const handleStartNewChat = useCallback(() => {
    startNewChat();
    clearChatSelection();
  }, [clearChatSelection, startNewChat]);
  const errorDescription = useMemo(() => {
    if (!error || !errorKind) {
      return null;
    }
    if (errorKind === "context-limit") {
      return (
        <Trans>
          The conversation is too long for this model. Start a new chat or use a
          model with a larger context window.
        </Trans>
      );
    }
    if (errorKind === "overloaded") {
      return (
        <Trans>
          This model is temporarily overloaded. Please switch to another model
          and try again.
        </Trans>
      );
    }
    if (rawUserVisibleErrors.has(error)) {
      return error;
    }
    return <Trans>An error occurred while sending the message.</Trans>;
  }, [error, errorKind, rawUserVisibleErrors]);
  const quotaStatusCardState = useMemo(
    () =>
      resolveChatQuotaStatusCardState({
        appError,
        currentModel: model,
        aiModelAccess,
        lowQuotaStatus,
        highQuotaStatus,
      }),
    [appError, model, aiModelAccess, lowQuotaStatus, highQuotaStatus],
  );
  const quotaStatusCard =
    lang && quotaStatusCardState.kind !== "hidden" ? (
      <ChatQuotaStatusCard
        appError={appError}
        currentModel={model}
        aiModelAccess={aiModelAccess}
        lowQuotaStatus={lowQuotaStatus}
        highQuotaStatus={highQuotaStatus}
        lang={lang}
        workspaceId={workspaceId}
        onModelChange={selectModel}
        className="w-full"
      />
    ) : null;
  const isCurrentModelQuotaExceeded = quotaStatusCardState.kind === "exceeded";
  const externalErrorCard = externalError ? (
    <ChatExternalErrorCard
      error={externalError}
      currentModel={model}
      aiModelAccess={aiModelAccess}
      onModelChange={selectModel}
      onStartNewChat={clearChatSelection}
      onDismiss={clearError}
      className="w-full"
    />
  ) : null;
  const statusCardContent =
    quotaStatusCard || externalErrorCard ? (
      <div className="space-y-1.5">
        {externalErrorCard}
        {quotaStatusCard}
      </div>
    ) : null;
  const scrollRequestKey =
    status === "submitted" && messages.at(-1)?.role === "user"
      ? (messages.at(-1)?.id ?? null)
      : null;
  const initialPositionKey =
    propChatId && messages.length > 0 ? propChatId : null;
  const isInitialPositionPending =
    initialPositionKey !== null &&
    positionedConversationKey !== initialPositionKey;
  const lastMessagePartCount = messages.at(-1)?.parts.length ?? 0;
  const {
    isOccluded: isDisclaimerOccluded,
    measureOcclusion: updateDisclaimerOcclusion,
  } = useChatDisclaimerOcclusion({
    rootRef: chatThreadRef,
    hasMessageContent: messages.length > 0 && lastMessagePartCount > 0,
    isObservationPaused: status === "submitted",
    retainOcclusion: status === "streaming",
  });

  return (
    <div
      ref={chatThreadRef}
      className="flex min-h-0 flex-1 flex-col"
      data-chat-thread=""
    >
      {/* 未保存かつメッセージがない draft だけに候補を表示する。 */}
      {messages.length === 0 && !activeChatId && !isCreating && (
        <div className="flex flex-1 flex-col items-center justify-center p-4">
          <ChatStarter
            model={model}
            aiModelAccess={aiModelAccess}
            onModelChange={selectModel}
            onStart={handleSend}
            compactWhenInactive={compactStarterWhenInactive}
            isCurrentModelQuotaExceeded={isCurrentModelQuotaExceeded}
            statusCard={statusCardContent}
            lowQuotaIndicator={lowQuotaIndicator}
            highQuotaIndicator={highQuotaIndicator}
            pathname={pathname}
            optionsState={optionsState}
            suppressPageContextAutoSyncOnCurrentPath={
              suppressPageContextAutoSyncOnCurrentPath
            }
          />
        </div>
      )}

      {/* 既存 chat の初期メッセージを取得している間の表示。 */}
      {messages.length === 0 && propChatId && isLoading && (
        <div className="flex flex-1 items-center justify-center p-4 text-muted-foreground text-sm">
          <Trans>Loading chat...</Trans>
        </div>
      )}

      {/* 会話内容がある場合、または入力を開始できる場合に thread を表示する。 */}
      {(messages.length > 0 || (activeChatId && !isLoading)) && (
        <>
          <Conversation
            className={`relative z-10 flex-1 @container ${
              isInitialPositionPending ? "pointer-events-none opacity-0" : ""
            }`}
            data-chat-container="conversation"
            initial="instant"
            resize="smooth"
            style={{ containerType: "size" }}
          >
            {({
              isAtBottom,
              scrollRef,
              scrollToBottom,
            }: StickToBottomContext) => (
              <>
                <ConversationContent
                  className={`${CHAT_SURFACE_CLASS_NAME} pt-3 pb-20 sm:pt-4 sm:pb-14`}
                  data-chat-surface="conversation"
                >
                  <ChatMessages
                    showResponseModel={currentUser.isAdmin === true}
                    messages={messages}
                    creatorName={finalCreatorName ?? undefined}
                    creatorPicture={finalCreatorPicture ?? undefined}
                    addToolResult={addToolResult}
                    addToolApprovalResponse={addToolApprovalResponse}
                    canRetryApprovalContinuation={canRetryApprovalContinuation}
                    isRetryingApprovalContinuation={
                      isRetryingApprovalContinuation
                    }
                    retryApprovalContinuation={retryApprovalContinuation}
                    retryableApprovalToolCallId={retryableApprovalToolCallId}
                    sendMessage={sendMessage}
                    requestApprovalContinuation={requestApprovalContinuation}
                    chatStatus={status}
                    isPersistingMessages={isPersistingMessages}
                    logContext={messageLogContext}
                  />
                </ConversationContent>
                <ConversationPositionReporter
                  initialPositionKey={initialPositionKey}
                  isAtBottom={isAtBottom}
                  onInitialPositioned={setPositionedConversationKey}
                  onChange={setIsConversationAtBottom}
                  onViewportChange={updateDisclaimerOcclusion}
                  scrollRequestKey={scrollRequestKey}
                  scrollRef={scrollRef}
                  scrollToBottom={scrollToBottom}
                />
                <ConversationScrollButton />
              </>
            )}
          </Conversation>

          <div
            className="shrink-0 bg-gradient-to-t from-background via-background to-background/80 pb-2 pt-3 @container sm:pb-3"
            data-chat-container="composer"
          >
            <div
              className={`${CHAT_SURFACE_CLASS_NAME} space-y-1`}
              data-chat-surface="composer"
            >
              {error && (
                <Alert variant="destructive" className="mb-2">
                  <AlertTitle className="flex justify-between">
                    <Trans>Error</Trans>
                    <Button
                      onClick={clearError}
                      variant="ghost"
                      className="size-6"
                    >
                      <XIcon />
                    </Button>
                  </AlertTitle>
                  <AlertDescription className="space-y-3">
                    <p>{errorDescription}</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleStartNewChat}
                      >
                        <Trans>Start a new chat</Trans>
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              <ChatComposer
                showDisclaimer={isConversationAtBottom && !isDisclaimerOccluded}
                inputHeader={
                  <div className="flex justify-end px-1">
                    <TokenUsageIndicator
                      usage={usage}
                      modelId={model}
                      status={status}
                    />
                  </div>
                }
                onAbort={stop}
                onSend={handleSend}
                status={status}
                isEditable={canSendMessages}
                model={model}
                aiModelAccess={aiModelAccess}
                onModelChange={selectModel}
                isCurrentModelQuotaExceeded={isCurrentModelQuotaExceeded}
                statusCard={statusCardContent}
                lowQuotaIndicator={lowQuotaIndicator}
                highQuotaIndicator={highQuotaIndicator}
                pathname={pathname}
                optionsState={optionsState}
                suppressPageContextAutoSyncOnCurrentPath={
                  suppressPageContextAutoSyncOnCurrentPath
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
